// Single source of truth for public-site metadata used by <Seo>, the footer, JSON-LD and the prerender/sitemap script.
export const SITE = {
  name: "HearthVault",
  tagline: "Ready when life happens.",
  executorTagline: "Ready for whoever needs it next.",
  url: (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, ""),
  description: "HearthVault turns your family's papers into a plan for before and after: powers of attorney, living will and DNR tracked and shared with the doctor, plus Social Security survivor-benefit estimates, ready-to-send claim and cancellation letters, Medicare and long-term care planning, and a step-by-step executor guide — with every document filed and encrypted.",
  ogImage: "/og.png",
  locale: "en_US",
  twitter: "",
  contactEmail: process.env.REACT_APP_CONTACT_EMAIL || "support@hearthvault.family",
  contactPath: "/support",
  statusUrl: "https://status.hearthvault.family",
  priceRange: "$0 – $18 / month",
};

// Public, indexable routes (also drives sitemap.xml + prerender).
export const PUBLIC_ROUTES = [
  { path: "/", priority: 1.0, changefreq: "weekly" },
  { path: "/tour", priority: 0.9, changefreq: "monthly" },
  { path: "/learn", priority: 0.9, changefreq: "weekly" },
  { path: "/faq", priority: 0.8, changefreq: "monthly" },
  { path: "/switch", priority: 0.8, changefreq: "monthly" },
  { path: "/for-advisors", priority: 0.8, changefreq: "monthly" },
  { path: "/trust", priority: 0.7, changefreq: "monthly" },
  { path: "/welcome", priority: 0.6, changefreq: "yearly" },
  { path: "/privacy", priority: 0.3, changefreq: "yearly" },
  { path: "/terms", priority: 0.3, changefreq: "yearly" },
];

export const SOCIAL_CHANNELS = [
  { key: "x", label: "X (Twitter)", placeholder: "https://x.com/hearthvault", claim: "https://x.com/i/flow/signup" },
  { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/hearthvault", claim: "https://www.facebook.com/pages/create" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/hearthvault", claim: "https://www.instagram.com/accounts/emailsignup/" },
  { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/hearthvault", claim: "https://www.linkedin.com/company/setup/new/" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@hearthvault", claim: "https://www.youtube.com/handle" },
  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@hearthvault", claim: "https://www.tiktok.com/signup" },
  { key: "pinterest", label: "Pinterest", placeholder: "https://pinterest.com/hearthvault", claim: "https://www.pinterest.com/business/create/" },
  { key: "threads", label: "Threads", placeholder: "https://threads.net/@hearthvault", claim: "https://www.threads.net/login" },
];
export const SOCIAL_HANDLE = "@hearthvault";

export const absUrl = (path = "/") => `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;

export const organizationJsonLd = (social = {}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  disambiguatingDescription: "Operator of the HearthVault family document vault at hearthvault.family. Not affiliated with Hearth Software.",
  logo: absUrl("/icons/icon-512.png"),
  contactPoint: { "@type": "ContactPoint", contactType: "customer support", url: absUrl(SITE.contactPath), availableLanguage: ["English", "Spanish"] },
  sameAs: Object.values(social || {}).filter(Boolean),
});

export const softwareJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  alternateName: "HearthVault family document vault",
  applicationCategory: "FinanceApplication",
  applicationSubCategory: "Family document vault & estate planning",
  operatingSystem: "Any (web browser); iOS and Android as a home-screen app",
  browserRequirements: "Any modern web browser. No download or installation.",
  description: SITE.description,
  disambiguatingDescription: "Cloud-hosted family document vault and estate-readiness service at hearthvault.family, unlocked with a PIN or passkey on any device. Nothing to install, no Microsoft Store listing, no home-network syncing between computers. Not affiliated with Hearth Software or the “Hearth” desktop backup app for Windows.",
  featureList: ["Upload, snap, email, fax or text documents in", "AI naming, filing and on-screen redaction", "Renewal reminders and monthly digest", "Estate Library and will worksheet", "Legacy letters and scheduled memories", "Verified executor access with owner veto", "Per-file AES-256-GCM encryption", "Encrypted AES-256 backup export"],
  isAccessibleForFree: true,
  softwareHelp: { "@type": "CreativeWork", url: absUrl("/how-it-works") },
  url: SITE.url,
  image: absUrl(SITE.ogImage),
  offers: [
    { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
    { "@type": "Offer", name: "Plus", price: "8", priceCurrency: "USD", priceSpecification: { "@type": "UnitPriceSpecification", price: "8", priceCurrency: "USD", billingDuration: "P1M" } },
    { "@type": "Offer", name: "Family", price: "18", priceCurrency: "USD", priceSpecification: { "@type": "UnitPriceSpecification", price: "18", priceCurrency: "USD", billingDuration: "P1M" } },
  ],
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: absUrl(it.path) })),
});

export const faqJsonLd = (qas) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qas.map((q) => ({ "@type": "Question", name: q.q, acceptedAnswer: { "@type": "Answer", text: q.a } })),
});
